import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../components/ui/sheet';
import {
  LayoutDashboard, Users, ClipboardList, Clock, Receipt, BarChart3,
  Settings, LogOut, Bell, Search, Plus, CalendarClock, TrendingUp,
  CheckCircle2, AlertTriangle, ArrowUp, MoreHorizontal, Loader2,
  Play, Square, Timer, FileText, Calendar as CalendarIcon, MessageSquare, Shield, LineChart, Landmark, FolderCheck, Wallet, UserPlus, Menu, BookCheck, Globe,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useChatNotifications } from '../context/ChatNotificationsContext';
import { auth, tasks, deadlines, dashboardApi, clients, clearSession, getStoredUser, team } from '../api';
import { brand } from '../mock';
import { GlobalSearch } from '../components/GlobalSearch';
import { NotificationBell } from '../components/NotificationBell';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
  { icon: ClipboardList, label: 'Tasks', to: '/dashboard/tasks' },
  { icon: CalendarIcon, label: 'Calendar', to: '/dashboard/calendar' },
  { icon: Users, label: 'Clients', to: '/dashboard/clients' },
  { icon: Globe, label: 'Client Portals', to: '/dashboard/portals' },
  { icon: UserPlus, label: 'Prospects', to: '/dashboard/prospects' },
  { icon: BookCheck, label: 'Accounting', to: '/dashboard/accounting' },
  { icon: FolderCheck, label: 'Documents', to: '/dashboard/documents' },
  { icon: Wallet, label: 'Payroll', to: '/dashboard/payroll' },
  { icon: Landmark, label: 'SARS', to: '/dashboard/sars' },
  { icon: Users, label: 'Team', to: '/dashboard/team' },
  { icon: MessageSquare, label: 'Chat', to: '/dashboard/chat' },
  { icon: Clock, label: 'Time', to: '/dashboard/time' },
  { icon: Receipt, label: 'Billing', to: '/dashboard/billing' },
  { icon: FileText, label: 'Templates', to: '/dashboard/templates' },
  { icon: BarChart3, label: 'Reports', to: '/dashboard/reports' },
  { icon: Settings, label: 'Settings', to: '/dashboard/settings' },
];

// Visible only to platform admins (jpstevens13@gmail.com)
const adminOnlyNavItems = [
  { icon: Shield, label: 'Access', to: '/dashboard/access' },
];

export function CategoryChip({ category }) {
  const map = {
    'VAT': 'bg-emerald-50 text-emerald-800 border-emerald-100',
    'PAYE': 'bg-blue-50 text-blue-800 border-blue-100',
    'CIPC': 'bg-purple-50 text-purple-800 border-purple-100',
    'Beneficial Ownership': 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-100',
    'Taxation': 'bg-amber-50 text-amber-800 border-amber-100',
    'Provisional Tax': 'bg-orange-50 text-orange-800 border-orange-100',
    'Management Accounts': 'bg-cyan-50 text-cyan-800 border-cyan-100',
    'Bookkeeping': 'bg-teal-50 text-teal-800 border-teal-100',
    'Audit': 'bg-red-50 text-red-800 border-red-100',
    'Advisory': 'bg-indigo-50 text-indigo-800 border-indigo-100',
    'Other': 'bg-stone-100 text-stone-700 border-stone-200',
  };
  const cls = map[category] || map['Other'];
  return <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${cls} whitespace-nowrap`}>{category || 'Other'}</span>;
}

export const TASK_CATEGORY_ORDER = ['All', 'VAT', 'PAYE', 'CIPC', 'Beneficial Ownership', 'Taxation', 'Provisional Tax', 'Management Accounts', 'Bookkeeping', 'Audit', 'Advisory', 'Other'];

function StatusPill({ status }) {
  const map = {
    'To Do': 'bg-stone-100 text-stone-700',
    'In Progress': 'bg-blue-50 text-blue-800',
    'Review': 'bg-amber-50 text-amber-800',
    'Done': 'bg-emerald-50 text-emerald-800',
  };
  return <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${map[status] || 'bg-stone-100'}`}>{status}</span>;
}

export function Sidebar({ activeLabel = 'Dashboard', mobile = false, onNavigate }) {
  const nav = useNavigate();
  const location = useLocation();
  const { unread: chatUnread } = useChatNotifications();
  const user = getStoredUser();
  const logout = () => { clearSession(); nav('/'); };
  const items = user?.isPlatformAdmin ? [...navItems, ...adminOnlyNavItems] : navItems;
  // Financials tab: owner always, or any member with seeFirmFinancials granted
  const canSeeFinancials = user?.firmRole === 'owner' || user?.permissions?.seeFirmFinancials === true || user?.isPlatformAdmin;
  const finalItems = canSeeFinancials
    ? [...items.slice(0, 11), { icon: LineChart, label: 'Financials', to: '/dashboard/financials' }, ...items.slice(11)]
    : items;
  const active = mobile
    ? ([...finalItems].sort((a, b) => b.to.length - a.to.length).find((n) => location.pathname === n.to || location.pathname.startsWith(`${n.to}/`))?.label || 'Dashboard')
    : activeLabel;
  return (
    <aside className={`${mobile ? 'flex w-full h-full' : 'hidden lg:flex w-64 min-h-screen'} flex-col bg-stone-950 text-stone-300 border-r border-stone-900`} data-testid={mobile ? 'mobile-sidebar' : 'sidebar'}>
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2 p-5 border-b border-stone-900">
        <div className="relative">
          <div className="pointer-events-none absolute -inset-1 rounded-lg bg-sky-500/30 blur-md"/>
          <div className="relative h-9 w-9 rounded-lg bg-black overflow-hidden grid place-items-center ring-1 ring-sky-500/40"><img src="/brand/accu-desk-logo.png" alt="Accu Desk" className="h-full w-full object-contain"/></div>
        </div>
        <div>
          <div className="font-semibold text-white text-sm">{brand.name}</div>
          <div className="text-[9px] uppercase tracking-wider text-stone-500">{brand.tagline}</div>
        </div>
      </Link>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {finalItems.map((n) => (
          <Link key={n.label} to={n.to} onClick={onNavigate} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${n.label === active ? 'bg-emerald-900/40 text-white' : 'text-stone-400 hover:bg-stone-900 hover:text-white'}`} data-testid={`${mobile ? 'mobile-' : ''}nav-${n.label.toLowerCase()}`}>
            <n.icon size={16}/>
            <span className="flex-1">{n.label}</span>
            {n.label === 'Chat' && chatUnread > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none ring-2 ring-stone-950" data-testid="chat-unread-badge">
                {chatUnread > 99 ? '99+' : chatUnread}
              </span>
            )}
            {n.label === 'Access' && (
              <span className="text-[9px] uppercase tracking-wider text-sky-300 bg-sky-500/15 px-1.5 py-0.5 rounded">Admin</span>
            )}
          </Link>
        ))}
      </nav>
      <button onClick={logout} className="m-3 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-stone-400 hover:bg-stone-900 hover:text-white">
        <LogOut size={16}/> Sign out
      </button>
    </aside>
  );
}

export function Topbar({ user, firm }) {
  const initials = (user?.name || 'A').split(' ').map((s) => s[0]).slice(0, 2).join('');
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-stone-200">
      <div className="h-16 px-4 lg:px-8 flex items-center gap-3 lg:gap-4">
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <button type="button" className="lg:hidden -ml-1 p-2 rounded-lg text-stone-700 hover:bg-stone-100" aria-label="Open menu" data-testid="mobile-menu-btn"><Menu size={22}/></button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 bg-stone-950 border-stone-900 [&>button]:text-white">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <Sidebar mobile onNavigate={() => setMenuOpen(false)}/>
          </SheetContent>
        </Sheet>
        <div className="lg:hidden hidden sm:flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-black overflow-hidden grid place-items-center"><img src="/brand/accu-desk-logo.png" alt="Accu Desk" className="h-full w-full object-contain"/></div>
          <span className="font-semibold text-stone-900">{brand.name}</span>
        </div>
        <GlobalSearch/>
        <div className="ml-auto flex items-center gap-2">
          <NotificationBell/>
          <div className="h-9 w-px bg-stone-200"/>
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-emerald-100 text-emerald-900 font-semibold">{initials || 'A'}</AvatarFallback>
          </Avatar>
          <div className="hidden sm:block leading-tight">
            <div className="text-sm font-medium text-stone-900">{user?.name}</div>
            <div className="text-xs text-stone-500">{firm}</div>
          </div>
        </div>
      </div>
    </header>
  );
}

const TASK_CATEGORIES = TASK_CATEGORY_ORDER.filter((c) => c !== 'All');

export function NewTaskDialog({ open, onOpenChange, clientsList, teamMembers, currentUserId, onCreated, defaultDate, task }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: '', clientId: '', assigneeId: currentUserId || '', status: 'To Do', dueDate: defaultDate || '', category: 'Other' });

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({ title: task.title || '', clientId: task.clientId || '', assigneeId: task.assigneeId || '', status: task.status || 'To Do',
        dueDate: task.dueDate ? String(task.dueDate).slice(0, 10) : '', category: task.category || 'Other' });
    } else {
      setForm((f) => ({ ...f, assigneeId: f.assigneeId || currentUserId || '', dueDate: defaultDate || f.dueDate }));
    }
  }, [open, currentUserId, defaultDate, task]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      payload.dueDate = form.dueDate ? new Date(form.dueDate).toISOString() : null;
      if (task) {
        payload.clientId = form.clientId || null;
        payload.clientName = (clientsList.find((c) => c.id === form.clientId) || {}).name || null;
        payload.assigneeId = form.assigneeId || null;
        payload.hours = task.hours || 0;
        const t = await tasks.update(task.id, payload);
        toast({ title: 'Task updated', description: t.title });
        onCreated?.();
        onOpenChange(false);
        return;
      }
      if (!payload.clientId) delete payload.clientId;
      if (!payload.assigneeId) delete payload.assigneeId;
      const t = await tasks.create(payload);
      toast({ title: 'Task created', description: t.title });
      onCreated?.();
      onOpenChange(false);
      setForm({ title: '', clientId: '', assigneeId: currentUserId || '', status: 'To Do', dueDate: '', category: 'Other' });
    } catch (err) {
      toast({ title: task ? 'Could not update task' : 'Could not create task', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{task ? 'Edit task' : 'Create task'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input id="title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="VAT201 submission" className="mt-1.5" data-testid="task-form-title-input"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="task-form-category-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {TASK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="task-form-status-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="To Do">To Do</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Review">Review</SelectItem>
                  <SelectItem value="Done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Client</Label>
              <Select value={form.clientId || 'none'} onValueChange={(v) => setForm({ ...form, clientId: v === 'none' ? '' : v })}>
                <SelectTrigger className="mt-1.5" data-testid="task-form-client-select"><SelectValue placeholder="Select client"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No client</SelectItem>
                  {clientsList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assignee</Label>
              <Select value={form.assigneeId || 'me'} onValueChange={(v) => setForm({ ...form, assigneeId: v === 'me' ? currentUserId : v })}>
                <SelectTrigger className="mt-1.5" data-testid="task-form-assignee-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {(teamMembers || []).map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}{m.id === currentUserId ? ' (You)' : ''}</SelectItem>
                  ))}
                  {(!teamMembers || teamMembers.length === 0) && (
                    <SelectItem value="me">You</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="dueDate">Due date</Label>
            <Input id="dueDate" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className="mt-1.5" data-testid="task-form-due-input"/>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="task-form-submit-btn">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin"/> : null} {task ? 'Save changes' : 'Create task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [deadlineList, setDeadlineList] = useState([]);
  const [taskList, setTaskList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [activeTimer, setActiveTimer] = useState(null); // { taskId, title, startedAt }
  const [nowTick, setNowTick] = useState(Date.now());

  const fetchAll = async () => {
    try {
      const [meRes, ov, dl, tl, cl, at, tm] = await Promise.all([
        auth.me().catch(() => null),
        dashboardApi.overview(),
        deadlines.list(),
        tasks.list(),
        clients.list(),
        tasks.activeTimer().catch(() => ({ active: null })),
        team.list().catch(() => ({ members: [] })),
      ]);
      if (meRes) setUser(meRes);
      setOverview(ov);
      setDeadlineList(dl);
      setTaskList(tl);
      setClientList(cl);
      setTeamMembers(tm.members || []);
      if (at?.active) {
        setActiveTimer({
          taskId: at.active.id,
          title: at.active.title,
          clientName: at.active.clientName,
          startedAt: at.active.timerStartedAt,
        });
      } else {
        setActiveTimer(null);
      }
    } catch (err) {
      // 401 interceptor handles redirect
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    fetchAll();
    // eslint-disable-next-line
  }, []);

  // Live tick every second while timer is active
  useEffect(() => {
    if (!activeTimer) return;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [activeTimer]);

  const startTimer = async (task) => {
    try {
      const updated = await tasks.startTimer(task.id);
      setActiveTimer({ taskId: updated.id, title: updated.title, clientName: updated.clientName, startedAt: updated.timerStartedAt });
      toast({ title: 'Timer started', description: updated.title });
      fetchAll();
    } catch (err) {
      toast({ title: 'Could not start timer', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const stopTimer = async (taskId) => {
    const id = taskId || activeTimer?.taskId;
    if (!id) return;
    try {
      const updated = await tasks.stopTimer(id);
      setActiveTimer(null);
      toast({ title: 'Timer stopped', description: `${updated.hours.toFixed(2)}h logged on "${updated.title}"` });
      fetchAll();
    } catch (err) {
      toast({ title: 'Could not stop timer', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const orig = taskList.find((t) => t.id === id);
      if (!orig) return;
      const payload = { title: orig.title, clientId: orig.clientId, clientName: orig.clientName, assigneeId: orig.assigneeId, status, dueDate: orig.dueDate, hours: orig.hours || 0, category: orig.category };
      const updated = await tasks.update(id, payload);
      setTaskList((prev) => prev.map((t) => (t.id === id ? updated : t)));
      toast({ title: 'Task updated', description: `"${orig.title}" → ${status}` });
      fetchAll();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading your practice...</div>
      </div>
    );
  }

  const displayName = user?.name || 'Friend';
  const firm = user?.firm || 'Your practice';
  const kpis = overview?.kpis || [];
  const weekly = overview?.weekly || { tasksDone: 0, totalTasks: 1, hoursLogged: 0, hoursTarget: 40, invoicesSent: 0, invoicesTarget: 20 };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Dashboard"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900">Good morning, {displayName.split(' ')[0]}.</h1>
              <p className="mt-1 text-stone-600 text-sm">Here&apos;s what your practice looks like today.</p>
            </div>
            <Button onClick={() => setTaskDialogOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 text-white">
              <Plus size={16} className="mr-2"/> New task
            </Button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((k) => (
              <div key={k.label} className="rounded-xl bg-white border border-stone-200 p-5 hover:shadow-md transition-shadow">
                <div className="text-xs text-stone-500 font-medium">{k.label}</div>
                <div className="mt-2 text-3xl font-serif font-semibold text-stone-900">{k.value}</div>
                <div className={`mt-1.5 text-xs font-medium inline-flex items-center gap-1 ${k.tone === 'warn' ? 'text-amber-700' : k.tone === 'good' ? 'text-emerald-700' : 'text-stone-600'}`}>
                  {k.tone === 'warn' ? <AlertTriangle size={12}/> : k.tone === 'good' ? <ArrowUp size={12}/> : <TrendingUp size={12}/>}
                  {k.delta}
                </div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-xl bg-white border border-stone-200 overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-stone-100">
                <div>
                  <div className="font-semibold text-stone-900">Upcoming SARS &amp; CIPC deadlines</div>
                  <div className="text-xs text-stone-500 mt-0.5">Auto-populated from your live tasks</div>
                </div>
                <Link to="#" onClick={(e) => e.preventDefault()}><Button variant="outline" size="sm">View all</Button></Link>
              </div>
              <div className="divide-y divide-stone-100">
                {deadlineList.length === 0 && <div className="p-6 text-center text-sm text-stone-500">No upcoming deadlines. Add a task with a due date to see it here.</div>}
                {deadlineList.map((d) => (
                  <div key={d.id} className="p-4 lg:p-5 flex items-center justify-between hover:bg-stone-50 transition-colors">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`h-11 w-11 rounded-lg grid place-items-center flex-shrink-0 ${d.days === 0 ? 'bg-red-50 text-red-700' : d.days <= 2 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                        <CalendarClock size={18}/>
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-stone-900 truncate">{d.task}</div>
                        <div className="text-sm text-stone-500 truncate">{d.client || 'No client'}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${d.days === 0 ? 'text-red-700' : d.days <= 2 ? 'text-amber-700' : 'text-stone-900'}`}>{d.due}</div>
                      <div className="text-xs text-stone-500">{d.days === 0 ? 'due now' : `${d.days} day${d.days === 1 ? '' : 's'} left`}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl bg-white border border-stone-200 p-5">
              <div className="font-semibold text-stone-900">This week</div>
              <div className="text-xs text-stone-500 mt-0.5">Practice at a glance</div>
              <div className="mt-5 space-y-5">
                <ProgressBar label="Tasks completed" current={weekly.tasksDone} total={weekly.totalTasks} color="bg-emerald-700"/>
                <ProgressBar label="Hours logged" current={weekly.hoursLogged} total={weekly.hoursTarget} suffix="h" color="bg-emerald-700"/>
                <ProgressBar label="Invoices sent" current={weekly.invoicesSent} total={weekly.invoicesTarget} color="bg-amber-500"/>
              </div>
              <div className="mt-6 rounded-lg bg-emerald-50 border border-emerald-100 p-4">
                <div className="flex items-center gap-2 text-emerald-900 font-medium text-sm"><CheckCircle2 size={16}/> On track for the week</div>
                <div className="mt-1 text-xs text-emerald-900/80">Every deadline is tracked in real time.</div>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="flex flex-col gap-3 p-5 border-b border-stone-100">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-stone-900">My open tasks</div>
                  <div className="text-xs text-stone-500 mt-0.5">Across all clients &middot; start a timer to log hours</div>
                </div>
                <Badge variant="outline" className="border-stone-300">{taskList.filter((t) => t.status !== 'Done' && (categoryFilter === 'All' || (t.category || 'Other') === categoryFilter)).length} open</Badge>
              </div>
              {/* Category filter tabs */}
              <div className="flex flex-wrap gap-1.5">
                {(() => {
                  const counts = { All: taskList.filter((t) => t.status !== 'Done').length };
                  taskList.filter((t) => t.status !== 'Done').forEach((t) => {
                    const c = t.category || 'Other';
                    counts[c] = (counts[c] || 0) + 1;
                  });
                  return TASK_CATEGORY_ORDER.filter((c) => c === 'All' || counts[c]).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCategoryFilter(c)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${categoryFilter === c ? 'bg-emerald-800 text-white border-emerald-800' : 'bg-white text-stone-700 border-stone-200 hover:border-emerald-500'}`}
                    >
                      {c} <span className={`ml-1 ${categoryFilter === c ? 'text-emerald-100' : 'text-stone-400'}`}>{counts[c] || 0}</span>
                    </button>
                  ));
                })()}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Task</th>
                    <th className="text-left px-5 py-3 font-medium">Category</th>
                    <th className="text-left px-5 py-3 font-medium">Client</th>
                    <th className="text-left px-5 py-3 font-medium">Assignee</th>
                    <th className="text-right px-5 py-3 font-medium">Hours</th>
                    <th className="text-left px-5 py-3 font-medium">Status</th>
                    <th className="text-center px-5 py-3 font-medium">Timer</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {(() => {
                    const rows = taskList.filter((t) => categoryFilter === 'All' || (t.category || 'Other') === categoryFilter);
                    if (rows.length === 0) {
                      return <tr><td colSpan={8} className="px-5 py-8 text-center text-stone-500">{taskList.length === 0 ? <>No tasks yet. Click <strong>New task</strong> to add one.</> : <>No tasks in this category.</>}</td></tr>;
                    }
                    return rows.map((t) => {
                    const isRunning = activeTimer?.taskId === t.id;
                    return (
                    <tr key={t.id} className={`transition-colors ${isRunning ? 'bg-emerald-50/50' : 'hover:bg-stone-50'}`}>
                      <td className="px-5 py-4 font-medium text-stone-900">
                        <div className="flex items-center gap-2">
                          {isRunning && <span className="h-2 w-2 rounded-full bg-emerald-600 animate-pulse"/>}
                          {t.title}
                        </div>
                      </td>
                      <td className="px-5 py-4"><CategoryChip category={t.category || 'Other'}/></td>
                      <td className="px-5 py-4 text-stone-700">{t.clientName || '—'}</td>
                      <td className="px-5 py-4 text-stone-700">{t.assignee}</td>
                      <td className="px-5 py-4 text-right tabular-nums text-stone-700">{(t.hours || 0).toFixed(2)}h</td>
                      <td className="px-5 py-4">
                        <Select value={t.status} onValueChange={(v) => changeStatus(t.id, v)}>
                          <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="To Do">To Do</SelectItem>
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="Review">Review</SelectItem>
                            <SelectItem value="Done">Done</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4 text-center">
                        {isRunning ? (
                          <button onClick={() => stopTimer(t.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 text-xs font-medium border border-red-100">
                            <Square size={12} className="fill-current"/> Stop
                          </button>
                        ) : (
                          <button onClick={() => startTimer(t)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-xs font-medium border border-emerald-100">
                            <Play size={12} className="fill-current"/> Start
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button onClick={async () => {
                          if (!window.confirm('Delete this task?')) return;
                          await tasks.remove(t.id);
                          toast({ title: 'Task deleted' });
                          fetchAll();
                        }} className="text-stone-400 hover:text-red-600"><MoreHorizontal size={16}/></button>
                      </td>
                    </tr>
                  );});})()}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {activeTimer && <LiveTimerWidget activeTimer={activeTimer} nowTick={nowTick} onStop={() => stopTimer()}/>}

      <NewTaskDialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen} clientsList={clientList} teamMembers={teamMembers} currentUserId={user?.id} onCreated={fetchAll}/>
    </div>
  );
}

function LiveTimerWidget({ activeTimer, nowTick, onStop }) {
  const started = activeTimer?.startedAt ? new Date(activeTimer.startedAt).getTime() : Date.now();
  const elapsed = Math.max(0, Math.floor((nowTick - started) / 1000));
  const hh = String(Math.floor(elapsed / 3600)).padStart(2, '0');
  const mm = String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  return (
    <div className="fixed bottom-6 right-6 z-40 rounded-xl shadow-2xl border border-emerald-200 bg-white overflow-hidden w-80">
      <div className="px-4 py-3 bg-gradient-to-r from-emerald-800 via-sky-800 to-emerald-900 text-white flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-white/15 grid place-items-center">
          <Timer size={16} className="animate-pulse"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-emerald-100">Timer running</div>
          <div className="text-sm font-medium truncate">{activeTimer.title}</div>
        </div>
      </div>
      <div className="px-4 py-4 flex items-center justify-between">
        <div>
          <div className="text-3xl font-serif font-semibold text-stone-900 tabular-nums tracking-tight">{hh}:{mm}:{ss}</div>
          <div className="text-xs text-stone-500 mt-0.5">{activeTimer.clientName || 'No client'}</div>
        </div>
        <button onClick={onStop} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium shadow-sm">
          <Square size={14} className="fill-current"/> Stop
        </button>
      </div>
    </div>
  );
}

function ProgressBar({ label, current, total, suffix = '', color = 'bg-emerald-700' }) {
  const pct = Math.min(100, Math.round((Number(current) / Number(total || 1)) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-stone-600">{label}</span>
        <span className="font-semibold text-stone-900">{current}{suffix} / {total}{suffix}</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-stone-100 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}
